/**
 * @module hasClientConfiguration
 */

import doesClientConfigurationExistInCache from './doesClientConfigurationExistInCache';
import doesClientConfigurationExistInStore from './doesClientConfigurationExistInStore';
import shouldRefreshClientConfiguration from './shouldRefreshClientConfiguration';
import refreshClientConfiguration from './refreshClientConfiguration';
import getClientConfigurationFromCache from '../selectors/getClientConfigurationFromCache';
import setClientConfiguration from '../mutators/setClientConfiguration';
import updateRequestState from '../mutators/updateRequestState';
import { ClientConfigurationRequestState } from '../store/schema/clientConfiguration';
import { loadThemeDefinitions } from 'owa-actionable-message-v2';
import getThemeDefinitions from '../selectors/getThemeDefinitions';
import { isFeatureEnabled } from 'owa-feature-flags';
import getClientConfigurationFromStore from '../selectors/getClientConfigurationFromStore';
import fetchAutodActionEndpoint from './fetchAutodActionEndpoint';

/**
 * @function
 * To check whether the config data is null or not.
 * @returns whether config data is not null.
 */
const isConfigDataNotNull = (): boolean => {
    const configCache = getClientConfigurationFromStore();
    return configCache.data !== null;
};

/**
 * @function
 * To check whether the client configuration is present or not
 * @returns whether the client configuration is present
 */

const hasClientConfiguration = (): boolean => {
    if (isFeatureEnabled('rp-actionableMessagesAutoD')) {
        fetchAutodActionEndpoint();
    }

    if (doesClientConfigurationExistInStore()) {
        if (shouldRefreshClientConfiguration()) {
            refreshClientConfiguration();
        }
        // here we dont need to add the else if statement to load the theme
        // since either the else if statement below will execute or the
        // refresh config statement. During refresh config, the themes will
        // automatically be loaded. Otherwise the else-if stmt of client
        // config refresh check will load the themes.

        return isConfigDataNotNull();
    } else if (doesClientConfigurationExistInCache()) {
        setClientConfiguration(getClientConfigurationFromCache());
        updateRequestState(ClientConfigurationRequestState.Resolved);
        if (shouldRefreshClientConfiguration()) {
            refreshClientConfiguration();
        }

        if (isFeatureEnabled('rp-actionableMessagesThemeConfig')) {
            const themes = getThemeDefinitions();
            loadThemeDefinitions(themes);
        }

        return isConfigDataNotNull();
    } else {
        refreshClientConfiguration();
        return false;
    }
};

export default hasClientConfiguration;
